import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import mime from 'mime';
import { PalantirAvatarComponent } from '../avatar/avatar.component';
import { PalantirCardComponent } from '../card/card.component';
import { PalantirIconComponent } from '../icon/icon.component';
import { PalantirProgressComponent } from '../progress-bar/progress-bar.component';
import type { PalantirProgressBarModeEnum } from '../progress-bar/progress-bar.type';
import type { AttachmentCard } from './attachment-card.types';

@Component({
  selector: 'palantir-attachment-card',
  standalone: true,
  templateUrl: './attachment-card.component.html',
  styleUrls: ['./attachment-card.component.scss'],
  imports: [
    PalantirCardComponent,
    PalantirAvatarComponent,
    PalantirProgressComponent,
    PalantirIconComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PalantirAttachmentCardComponent {
  @Input() public attachment: AttachmentCard = {} as AttachmentCard;

  @Input() public loading?: {
    mode: PalantirProgressBarModeEnum;
    progress: number;
  };

  @Input() public activatable: boolean = false;

  @Output() public attachmentClick = new EventEmitter<string>();

  @HostBinding('class.activatable') public get activatableClass() {
    return this.activatable;
  }

  private readonly imageExtensions = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'tiff',
    'svg',
    'webp',
    'heic',
    'heif',
  ];

  private readonly videoExtensions = ['mp4', 'avi', 'mov', 'mkv', 'webm'];

  private readonly audioExtensions = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'];

  private readonly documentExtensions = {
    excel: ['xls', 'xlsx', 'ods'],
    word: ['doc', 'docx', 'odt', 'rtf'],
    presentation: ['ppt', 'pptx', 'odp'],
    text: ['txt', 'md', 'xml', 'json'],
  };

  private readonly cadExtensions = ['dwg', 'rvt', 'dxf'];

  private readonly archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz'];

  public get isMedia(): boolean {
    const mediaExtensions = [...this.imageExtensions, ...this.videoExtensions];
    return mediaExtensions.includes(this.extension);
  }

  public get isWord(): boolean {
    return this.documentExtensions.word.includes(this.extension);
  }

  public get isExcel(): boolean {
    return this.documentExtensions.excel.includes(this.extension);
  }

  public get isPresentation(): boolean {
    return this.documentExtensions.presentation.includes(this.extension);
  }

  public get isText(): boolean {
    return this.documentExtensions.text.includes(this.extension);
  }

  public get isPdf(): boolean {
    return this.extension === 'pdf';
  }

  public get isCsv(): boolean {
    return this.extension === 'csv';
  }

  public get isDefault(): boolean {
    return this.extension === 'default';
  }

  public get isAudio(): boolean {
    return this.audioExtensions.includes(this.extension);
  }

  public get isArchive(): boolean {
    return this.archiveExtensions.includes(this.extension);
  }

  public get isCad(): boolean {
    return this.cadExtensions.includes(this.extension);
  }

  public isImage(extension: string): boolean {
    return this.imageExtensions.includes(extension);
  }

  public isVideo(extension: string): boolean {
    return this.videoExtensions.includes(extension);
  }

  public onAttachmentClick(id: string): void {
    this.attachmentClick.emit(id);
  }

  public get fileUrl(): string | null {
    if (!this.attachment.fileUrl) {
      return null;
    }

    const mimeType = mime.getType(this.attachment.fileUrl);

    if (mimeType?.includes('video')) {
      if (!this.extension) {
        return null;
      }

      return this.attachment.fileUrl.replace(this.extension, 'jpg');
    }

    return this.attachment.fileUrl;
  }

  public get extension(): string {
    try {
      if (this.attachment.extension) {
        return this.attachment.extension as string;
      }

      const url = new URL(this.attachment.fileUrl);
      const pathname = url.pathname;
      return pathname.split('.').pop() ?? '';
    } catch {
      return 'default';
    }
  }
}
